/**
 * TOTP (RFC 6238) sobre HMAC (RFC 4226). Genera los códigos de un solo uso a
 * partir de una semilla base32 (el formato de `otpauth://` de los autenticadores).
 *
 * Compatible con Google Authenticator / Authy (SHA-1, 6 dígitos, periodo 30 s
 * por defecto). El cálculo es local: la semilla vive cifrada dentro del item.
 */

import { base32Decode } from "./encoding";
import { hmac } from "./primitives";

export interface TotpConfig {
  readonly digits?: number;
  readonly period?: number;
  readonly algorithm?: "SHA-1" | "SHA-256" | "SHA-512";
  /** Milisegundos; por defecto Date.now(). */
  readonly timestamp?: number;
}

function counterBytes(counter: number): Uint8Array {
  const buf = new Uint8Array(8);
  // Contador de 64 bits big-endian (los altos ~0 en la práctica).
  let value = counter;
  for (let i = 7; i >= 0; i--) {
    buf[i] = value & 0xff;
    value = Math.floor(value / 256);
  }
  return buf;
}

/** Código TOTP actual para la semilla base32 dada. */
export async function generateTotp(secretBase32: string, config: TotpConfig = {}): Promise<string> {
  const { digits = 6, period = 30, algorithm = "SHA-1", timestamp = Date.now() } = config;
  const key = base32Decode(secretBase32);
  const counter = Math.floor(timestamp / 1000 / period);
  const mac = await hmac(key, counterBytes(counter), algorithm);
  // Truncamiento dinámico (RFC 4226 §5.3).
  const offset = (mac.at(-1) ?? 0) & 0x0f;
  const binary =
    (((mac[offset] ?? 0) & 0x7f) << 24) |
    (((mac[offset + 1] ?? 0) & 0xff) << 16) |
    (((mac[offset + 2] ?? 0) & 0xff) << 8) |
    ((mac[offset + 3] ?? 0) & 0xff);
  return (binary % 10 ** digits).toString().padStart(digits, "0");
}

/** Segundos restantes de validez del código actual (para la UI de cuenta atrás). */
export function totpTimeRemaining(config: Pick<TotpConfig, "period" | "timestamp"> = {}): number {
  const { period = 30, timestamp = Date.now() } = config;
  return period - (Math.floor(timestamp / 1000) % period);
}

/** Extrae la semilla base32 de un URI `otpauth://totp/...?secret=XXXX`. */
export function parseOtpauthSecret(uri: string): string | undefined {
  const match = uri.match(/[?&]secret=([^&]+)/i);
  return match ? match[1] : undefined;
}
